/**
 * Global freshness signal for the entire USM application.
 *
 * WHY THIS EXISTS
 * ---------------
 * USM pages fetch their data on `useEffect` mount and never listen for
 * `visibilitychange` / `focus` / `online` events. That means Device B
 * sitting on an already-loaded page will keep showing the last-fetched
 * snapshot even when Device A modifies data server-side — the user is
 * forced to F5 to see current state.
 *
 * HTTP caching is NOT the cause: the backend already sends
 * `Cache-Control: no-store, no-cache, must-revalidate` and Cloudflare
 * reports `cf-cache-status: DYNAMIC`. React Query is installed but not
 * used anywhere in USM. So the gap is purely: "no page listens for
 * wake-up events".
 *
 * HOW THIS FIXES IT
 * -----------------
 * The `useUsmFreshnessKey()` hook returns a monotonically-increasing
 * integer that bumps whenever ONE of these happens:
 *
 *   1) The tab becomes visible again after having been hidden for
 *      more than `MIN_HIDDEN_MS` (default 15 s). Prevents spamming
 *      the network for tiny alt-tabs.
 *   2) The window regains focus after being blurred for >
 *      `MIN_HIDDEN_MS`.
 *   3) The browser reports it's back online (`window.online`).
 *
 * `UsmLayout` applies this integer as the `key` on the `<Outlet />`
 * subtree. When the key changes, React remounts the current route
 * subtree only — layout, auth, messenger WS, permissions and every
 * side-effect that lives OUTSIDE the route (branch context, tablet
 * mode, sidebar prefs) all stay alive. Every USM page's mount-time
 * `useEffect(fetch, [])` re-runs and produces fresh data.
 *
 * PERMISSIONS / BRANCH SCOPING
 * ----------------------------
 * The refetch uses the same authenticated `usmApi` axios instance the
 * page already uses. Branch scoping is enforced by the backend on
 * every call and is completely untouched by this hook.
 *
 * NO POLLING
 * ----------
 * Zero setInterval / setTimeout in this file. It fires ONLY on real
 * user/OS events. If the operator never leaves the tab, this hook
 * never triggers a refetch.
 */
import { useEffect, useRef, useState, useCallback } from "react";

const MIN_HIDDEN_MS = 15_000;

export default function useUsmFreshnessKey() {
  const [key, setKey] = useState(0);
  const hiddenAtRef = useRef(null);
  const lastBumpRef = useRef(0);

  const bump = useCallback(() => {
    // Coalesce back-to-back triggers (visibilitychange + focus often
    // fire on the same wake) into a single remount.
    const now = Date.now();
    if (now - lastBumpRef.current < 500) return;
    lastBumpRef.current = now;
    setKey((k) => k + 1);
  }, []);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        hiddenAtRef.current = Date.now();
        return;
      }
      // Visible: only remount if we were away long enough.
      const startedHiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (startedHiddenAt && (Date.now() - startedHiddenAt) >= MIN_HIDDEN_MS) {
        bump();
      }
    };
    const onFocus = () => {
      // Focus without a prior visibility hide (e.g. alt-tab on desktop):
      // still refetch if the last bump was long ago.
      if (hiddenAtRef.current) return;
      if (Date.now() - lastBumpRef.current >= MIN_HIDDEN_MS) bump();
    };
    const onOnline = () => bump();

    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
    };
  }, [bump]);

  return key;
}
